import type { Design } from "../neon/Design";
import type { DesignData } from "../neon/types";

const MAX_DEPTH = 50;
const COMMIT_DEBOUNCE_MS = 220;

/**
 * Snapshot-based undo/redo for the Design. We rely on DesignData being a
 * cheap JSON blob — taking a deep clone per commit is fine up to a few
 * dozen tubes. Edits arrive via `Design.subscribe`, but we debounce so a
 * drag (which fires touch() per-frame) collapses into one history entry.
 */
export class History {
  private stack: string[] = [];
  private pointer = -1;
  private debounceId = 0;
  private suspend = false;
  private listeners = new Set<() => void>();

  constructor(private readonly design: Design) {
    this.commitNow();
    design.subscribe(() => this.scheduleCommit());
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  canUndo(): boolean {
    return this.pointer > 0;
  }

  canRedo(): boolean {
    return this.pointer < this.stack.length - 1;
  }

  undo(): boolean {
    // Flush any in-flight edit so the user can undo edits made within the
    // debounce window. Without this, "draw + immediate Cmd+Z" silently fails.
    this.commit();
    if (!this.canUndo()) return false;
    this.pointer--;
    this.restore(this.stack[this.pointer]);
    return true;
  }

  redo(): boolean {
    if (!this.canRedo()) return false;
    this.pointer++;
    this.restore(this.stack[this.pointer]);
    return true;
  }

  commit(): void {
    this.flushDebounce();
    this.commitNow();
  }

  reset(): void {
    this.flushDebounce();
    this.stack = [];
    this.pointer = -1;
    this.commitNow();
  }

  private scheduleCommit(): void {
    if (this.suspend) return;
    clearTimeout(this.debounceId);
    this.debounceId = window.setTimeout(() => this.commitNow(), COMMIT_DEBOUNCE_MS);
  }

  private flushDebounce(): void {
    if (this.debounceId) {
      clearTimeout(this.debounceId);
      this.debounceId = 0;
    }
  }

  private commitNow(): void {
    const snap = JSON.stringify(this.design.toData());
    // Skip no-op snapshots so undo doesn't have to step over duplicates.
    if (this.stack[this.pointer] === snap) return;
    // Drop any redo stack — new edit invalidates the forward history.
    this.stack.length = this.pointer + 1;
    this.stack.push(snap);
    if (this.stack.length > MAX_DEPTH) {
      this.stack.shift();
    } else {
      this.pointer++;
    }
    this.notify();
  }

  private restore(snap: string): void {
    this.suspend = true;
    try {
      const data = JSON.parse(snap) as DesignData;
      this.design.loadData(data);
    } finally {
      this.suspend = false;
    }
    this.notify();
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }
}
